import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import StockReport from "./StockReport";
import Moment from "react-moment";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  currencySymbolLeft,
  currencySymbolRight,
  checkPermission
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { MdDelete } from 'react-icons/md';

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import ViewAccountSheet from "../../accounts/transactions/ViewAccountSheet"
import KdsModal from "../../kitchen/KdsModal";
import CsvImportExport from "../../../../../components/CsvImportExport";
import DataTable from "../../../../common/DataTable";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const IngredientPurchaseHistory = () => {
  const { t } = useTranslation();
  //getting context values here

  let {
    //common
    loading,
    setLoading,

    //pagination
    dataPaginating,
    setDataPaginating,

    //ingredient purchases
    ingredientPurchaseHistory,
    setIngredientPurchaseHistory,
    setPaginatedIngredientPurchase,
    ingredientPurchaseHistoryForSearch,
    setIngredientPurchaseHistoryForSearch,
    branchForSearch,
  } = useContext(RestaurantContext);

  let { authUserInfo, getSupplier, supplierForSearch } = useContext(UserContext);

  // States hook here
  const [itemData, setItemData] = useState({
    modal: false,
    url: "",
    name: "",
    token: ""
  });

  const [ledgerData, setLedgerData] = useState({
    modal: false,
    ledger: null
  });

  const [actionLoading, setActionLoading] = useState(false);

  //search result
  let [searchedSupplier, setSearchedSupplier] = useState({
    list: null,
    searched: false,
  });

  const [btnDisable, setBtnDisable] = useState("")

  const searchInputRef = useRef(null);

  // image pop up
  const handleOpenPopup = (item) => {
    setItemData({
      modal: true,
      url: BASE_URL + item?.pi_document,
      token: `PI Document: ${item.invoice_number}`,
    });
  };

  const handleClosePopup = () => {
    setItemData({
      modal: false,
      url: "",
      name: "",
      token: "",
    });
  };

  //useEffect == componentDidMount
  useEffect(() => {
    setPaginatedIngredientPurchase(1);
    if (!supplierForSearch) {
      getSupplier();
    }
  }, []);

  //search purchase here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedSupplier({ ...searchedSupplier, searched: false });
    } else {
      const searchedList = ingredientPurchaseHistoryForSearch.filter((item) => {
        let lowerCaseItemSupplierName = item.supplier_name.toLowerCase();
        let lowerCaseItemInvoice = item.invoice_number !== null && item.invoice_number.toLowerCase();
        let lowerCaseBranch = item.branch?.name && item.branch.name.toLowerCase();

        return (
          lowerCaseItemSupplierName.includes(searchInput) ||
          lowerCaseItemInvoice.includes(searchInput) ||
          lowerCaseBranch.includes(searchInput)
        );
      });
      setSearchedSupplier({
        ...searchedSupplier,
        list: searchedList,
        searched: true,
      });
    }
  };

  const onClearSearch = () => {
    setSearchedSupplier({ ...searchedSupplier, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  //delete confirmation modal of waiter
  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{t("Are you sure?")}</h1>
            <p className="text-center">{t("You want to delete this?")}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary rounded"
                onClick={() => {
                  handleDeletePurchase(id);
                  onClose();
                }}
              >
                {t("Yes, delete it!")}
              </button>
              <button className="btn btn-success ml-2 px-3 rounded" onClick={onClose}>
                {t("No")}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  //delete purchase here
  const handleDeletePurchase = (id) => {
    setLoading(true);
    const url = BASE_URL + `/settings/delete-ingredient_purchase/${id}`;
    return axios
      .get(url, {
        headers: { Authorization: `Bearer ${getCookie()} ` },
      })
      .then((res) => {
        setIngredientPurchaseHistory(res.data[0]);
        setIngredientPurchaseHistoryForSearch(res.data[1]);
        setSearchedSupplier({
          ...searchedSupplier,
          list: res.data[1],
        });
        setLoading(false);
        ToastSuccess({ title: t("Purchase has been deleted successfully") });
      })
      .catch(() => {
        setLoading(false);
        ToastError({ title: t("Please try again") });
      });
  };

  //print bills states and function
  const [newRow, setNewRow] = useState([]);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  let [newItem, setNewItem] = useState({
    branch: null,
    supplier: null,
    invoice: "",
    description: "",
    paymentType: "",
  });

  const componentRef = useRef();

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@page { margin: 15px }"
  });

  const handleSetCsvResponse = useCallback((data) => {
    setIngredientPurchaseHistory(data[0]);
    setIngredientPurchaseHistoryForSearch(data[1]);
    setSearchedSupplier({
      ...searchedSupplier,
      list: data[1],
    });
  }, [])

  const handlePiLedger = async  (id) =>  {
    setActionLoading(true);
    const url = BASE_URL + "/settings/check-financial-ledger-view/" + parseInt(id) + "/PI";
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()} ` },
      });
      if (res.data) {
        setLedgerData({ modal: true, ledger: res.data });
      if (res.data) {
        setLedgerData({ modal: true, ledger: res.data });
      }
      setActionLoading(false);
    }
  } catch (err) {
      console.log("error==>", err);
      setActionLoading(false);
    }
  }

  const handleGetPrintData = async (id) => {
    const url = BASE_URL + "/settings/get-ingredient_purchase_items/" + parseInt(id);
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        let purchaseDate = new Date(res.data[1].purchase_date);
        setPurchaseDate(purchaseDate);
        let branch = branchForSearch?.find((item) => item.id === res.data[1].branch_id);
        let supplier = supplierForSearch?.find((item) => item.id === res.data[1].supplier_id);
        setNewItem({
          ...newItem,
          branch,
          description: res.data[1].desc,
          paymentType: res.data[1].payment_type,
          supplier,
          invoice: res.data[1].invoice_number,
          ref_doc: res.data[1].ref_doc_identity,
          transportation: res.data[1].transportation,
          advance_tax: res.data[1].total_advance_tax,
          net_amount: res.data[1].net_amount,
          total_amount: res.data[1].total_amount,
          total_discount: res.data[1].total_discount,
          total_tax: res.data[1].total_tax,
          total_bill: res.data[1].total_bill,
        });
        if (res.data[0] && res.data[0].length > 0) {
          setNewRow([...res.data[0]]);
        }
      }
      handleOnlyPrint();
    } catch (error) {
      console.log("error==>", error);
    }
  };

  const handleGetAmount = (index) => {
    let qty = 0;
    let rate = 0;
    if (newRow !== null && newRow[index].qty !== "") {
      qty = parseFloat(newRow[index].qty);
    }
    if (newRow !== null && newRow[index].rate !== "") {
      rate = parseFloat(newRow[index].rate);
    }
    return rate * qty;
  };

  //post button function
  const handlePostApi = async (item) => {
    // setActionLoading(true);
    setBtnDisable(item.id)
    const url = BASE_URL + "/settings/mark-is-post-purchase-invoice";
    let formData = new FormData();
    formData.append('id', item.id);
    formData.append('is_post', item.is_post ? 0 : 1);
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()} ` },
      });
      if (res.data) {
        setIngredientPurchaseHistory(res.data[0]);
        setIngredientPurchaseHistoryForSearch(res.data[1]);
        setSearchedSupplier({
          ...searchedSupplier,
          list: res.data[1],
        });
        ToastSuccess({
          title: t(item?.is_post ? "Purchase Invoice has been unpost" : "Purchase Invoice has been post")
        });
      }
      setActionLoading(false);
    } catch (err) {
      setActionLoading(false);
      if (err.response?.data?.status === "error") {
        ToastError({ title: t(err.response.data.message) });
        return;
      }
      ToastError({ title: t("Please try again") });
    }
  };

  //unpost confirmation modal
  const handleUnPostConfirmation = (item) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body px-5">
            <h3>{t("Are you sure?")}</h3>
            <p className="text-center">{t("You want to un post this?")}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handlePostApi(item);
                  onClose();
                }}
              >
                {t("Yes, unpost it!")}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {t("No")}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  // DataTable helper functions
  const handleFetchData = (page) => {
    setPaginatedIngredientPurchase(page);
  };

  const getListData = useCallback(() => {
    if (searchedSupplier.searched) {
      return searchedSupplier.list;
    }
    return ingredientPurchaseHistory?.data || [];
  }, [searchedSupplier, ingredientPurchaseHistory]);

  const csvData = [
    {
      key: "id",
      index: 0,
    },
    {
      key: "branch_name",
      index: 1,
    },
    {
      key: "supplier_name",
      index: 2,
    },
    {
      key: "ref_no",
      index: 3,
    },
    {
      key: "manual_invoice_no",
      index: 4,
    },
    {
      key: "payment_type",
      index: 5,
    },
    {
      key: "credit_limit_days",
      index: 6,
    },
    {
      key: "remarks",
      index: 7,
    },
    {
      key: "item_name",
      index: 8,
    },
    {
      key: "qty",
      index: 9,
    },
    {
      key: "unit",
      index: 10,
    },
    {
      key: "rate",
      index: 11,
    },
    {
      key: "discount_percent",
      index: 12,
    },
    {
      key: "tax_percent",
      index: 13,
    }
  ]

  const [multiPi, setMultiPi] = useState({
    piItems: null,
    is_post: 1,
  });

  const toggleSelectAll = (e) =>{
    e.stopPropagation();
    let updated = {...multiPi.piItems};
    if(e.target.checked){
      ((searchedSupplier?.searched && searchedSupplier?.list) || ingredientPurchaseHistory.data).forEach((item)=>{
        if(!item.is_post){
          updated[item.id] = item.id;
        }
      })
    }else{
      ((searchedSupplier?.searched && searchedSupplier?.list) || ingredientPurchaseHistory.data).forEach((item)=>{
        if(updated[item.id]){
          delete updated[item.id];
        }
      })
    }

    setMultiPi((curr)=>({
      ...curr,
      piItems: updated
    }));
  }
  
  const toggleSelect = (id) => {
    let updated = {...multiPi.piItems};
    if (updated[id]) {
      delete updated[id];
    } else {
      updated[id] = id;
    }
    setMultiPi((curr)=>({
      ...curr,
      piItems: updated
    }));
  };

  //multi post
  const handleSavemultiPi = async (e) => {
    e.preventDefault();
    if(!multiPi?.piItems || !Object.values(multiPi?.piItems)?.length){
      ToastError({ title: `${_t(t("Please select item to post"))}` });
      return false;
    }
    setDataPaginating(true);

    const url = BASE_URL + "/settings/multi-post-purchase-invoice";
    const formData = {
      purchaseInvoiceId: multiPi.piItems
        ? Object.values(multiPi.piItems)
        : [],
      is_post: multiPi.is_post ?? 1,
    };
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setDataPaginating(false)
        setIngredientPurchaseHistory(res.data[0])
        setIngredientPurchaseHistoryForSearch(res.data[1]);
        setSearchedSupplier({
          ...searchedSupplier,
          list: res.data[1],
        });
        setMultiPi({
          piItems: null,
          is_post: 1,
        });
        ToastSuccess({ title: `${_t(t("Cash Payment has been posted"))}` });
        if(document.getElementById("multi_cash_check")){
          document.getElementById("multi_cash_check").checked = false;
        }
      }
    } catch (error) {
      setDataPaginating(false);
      if (error?.response?.data && error.response.data?.status == 0) {
        ToastError({ title: `${_t(t(error.response.data.message))}` });
        return false;
      }
      ToastError({ title: "Please try again" });
    }
  };

  const checkPiPost = useMemo(()=>{
    const permissions = authUserInfo?.permissions || [];
    return checkPermission(permissions, "PI Post");
  },[authUserInfo?.permissions])

  const COLUMNS = [
    {
      label: "S/L",
      render: (item, index) => {
        if (searchedSupplier.searched) {
          return index + 1;
        }
        return index + 1 + (ingredientPurchaseHistory.current_page - 1) * ingredientPurchaseHistory.per_page;
      },
    },
    ...(checkPiPost ? [
      {labelRender:()=>{
        return (
          <div className="d-flex justify-content-center align-items-center">
            <button disabled={!checkPiPost} className="btn btn-sm btn-dark mr-2" onClick={(e)=>handleSavemultiPi(e)}>
              <i className="fa fa-check-square-o "></i>
            </button>
            <input
              id="multi_cash_check"
              type="checkbox"
              className="form-check-input table-text"
              onChange={(e) => {
                toggleSelectAll(e);
              }}
            />
          </div>
        )
      },
      render:(item)=>{
        return (
          <input
            disabled={item.is_post}
            id={item.id}
            type="checkbox"
            className="form-check-input table-text"
            checked={multiPi?.piItems && (multiPi.piItems[item.id] ?? 0)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              toggleSelect(item.id)
            }}
          />
        )
      }}
    ] : []),
    {
      label: "Supplier",
      key: "supplier_name",
    },
    {
      label: "Invoice",
      key: "invoice_number",
    },
    {
      label: "Grn Ref",
      render: (item) => (
        <>
          {item?.ref_doc_identity && item?.ref_doc_identity !== "0" ? 
            item?.ref_doc_identity : "-"}
        </>
      )
    },
    {
      label: "Purchased",
      render: (item) => (
        <Moment format="DD-MMMM-YYYY">
          {item.purchase_date}
        </Moment>
      )
    },
    {
      label: "Branch",
      render: (item) => item.branch?.name
    },
    {
      label: "Total",
      render: (item) => (
        <>
          {currencySymbolLeft()}
          {formatPrice(item.total_bill)}
          {currencySymbolRight()}
        </>
      )
    },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">
          {item.is_post !== 1 && (
            <NavLink
              title="Edit"
              disabled={item.id === btnDisable}
              className="btn btn-primary ml-2 rounded-sm del-btn"
              to={`/dashboard/manage/stock/purchase-invoice-edit/${item.id}`}
              style={{ height: "1.7rem", width: "1.7rem" }}
            >
              <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
            </NavLink>
          )}
          <NavLink
            title="duplicate invoice purchase"
            to={`/dashboard/manage/stock/purchase-invoice-edit/${item.id}?duplicate=1`}
            className="btn btn-success ml-2 rounded-sm del-btn"
          >
            <i className="fa fa-clone"></i>
          </NavLink>
          {item.is_post !== 1 && (
            <button
              disabled={item.id === btnDisable}
              className="btn btn-danger ml-2 rounded-sm del-btn"
              onClick={() => handleDeleteConfirmation(item.id)}
            >
              <MdDelete className="del-icon" />
            </button>
          )
          }
          {authUserInfo.permissions !== null && authUserInfo.permissions.includes("Accounts") &&
            item.is_post === 1 && (
              <button
                title="PI Ledger"
                className="btn btn-primary ml-2 rounded-sm del-btn"
                onClick={() => handlePiLedger(item.id)}
              >
                <i className="fa fa-eye" aria-hidden="true"></i>
              </button>
            )
          }
          <button
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handleGetPrintData(item.id)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {(checkPiPost && item.is_post !== 1 ? <button
            className="btn btn-dark ml-2 rounded-sm del-btn"
            title="Post PI"
            style={{ padding: "1px 0px 0px 1px" }}
            disabled={loading || item.id === btnDisable}
            onClick={() => handlePostApi(item)}
          >
            <i className="fa fa-check-square-o" aria-hidden="true"></i>
          </button> : null)
          }
          {(checkPiPost && authUserInfo?.details?.user_type === "superAdmin" && item.is_post === 1 ? <button
            className="btn btn-warning ml-2 rounded-sm del-btn"
            title="Unpost PI"
            style={{ padding: "1px 0px 0px 1px" }}
            disabled={loading || item.id === btnDisable}
            onClick={() => handleUnPostConfirmation(item)}
          >
            <i className="fa fa-check-square-o" aria-hidden="true"></i>
          </button> : null)
          }
          {item?.pi_document &&
            <button
              title="Pi document"
              className="btn btn-light ml-2 rounded-sm del-btn"
              onClick={() => handleOpenPopup(item)}
            >
              <i className="fa fa-eye"></i>
            </button>
          }
        </div>
      )
    },
    {
      label: "Created Date",
      render: (item) => (
        <Moment format="DD-MMMM-YYYY hh:mm A">
          {item?.created_at}
        </Moment>
      )
    },
    {
      label: "Created By",
      render: (item) => <>{item?.created_by_name || "-"}</>
    },
    {
      label: "Updated By",
      render: (item) => <>{item?.updated_by_name || "-"}</>
    },
    {
      label: "Posted By",
      render: (item) => <>{item?.post_by_name || "-"}</>
    },
  ]

  return (
    <>
      <Helmet>
        <title>{_t(t("Ingredient Purchases"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div key="fragment3">

                <div className="t-bg-white">
                  <div className=" row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-7 col-lg-6 t-mb-15 mb-md-0 d-flex justify-content-between align-items-center">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {t("Purchase Invoice List")}
                          </span>
                        </li>
                      </ul>
                      <CsvImportExport
                        filename="PI Sample Format CSV"
                        csvLength={14}
                        exportUrl="/settings/export-purchase-invoice"
                        importUrl="/settings/upload-purchase-invoice"
                        exportBtnName="Sample Format CSV"
                        csvData={csvData}
                        onCsvResponse={handleSetCsvResponse}
                      />
                    </div>
                    <div className="col-md-5 col-lg-6">
                      <div className="row gx-3 align-items-center">
                        {/* Search customer */}
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                ref={searchInputRef}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i
                                className="fa fa-search"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Add customer modal trigger button */}
                        <div className="col-md-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/stock/purchase-invoice-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {_t(t("add new"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  loading={loading || dataPaginating}
                  data={getListData()}
                  columns={COLUMNS}
                  emptyText={_t(t("No data available"))}
                  pagination={{
                    isPageLoading: loading,
                    isPagination: true,
                    pageCount: ingredientPurchaseHistory?.last_page,
                    currentPage: ingredientPurchaseHistory?.current_page,
                    from: ingredientPurchaseHistory?.from,
                    to: ingredientPurchaseHistory?.to,
                    total: ingredientPurchaseHistory?.total,
                  }}
                  handleFetchData={setPaginatedIngredientPurchase}
                  searchPayload={{
                    isSearched: searchedSupplier.searched,
                    onClearSearch,
                  }}
                />

              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>

      <KdsModal
        show={itemData.modal}
        handleClose={handleClosePopup}
        itemData={itemData}
      />

      {/* main body ends */}

      {/* Print component */}
      <StockReport
        name="Purchase Invoice"
        purchaseDate={purchaseDate}
        newItem={newItem}
        newRow={newRow}
        invoiceNo={"Invoice No"}
        inoviceDate={"Invoice Date"}
        handleGetAmount={handleGetAmount}
        stockPrint={componentRef}
      />

      {/* Ledger view modal */}
      <ViewAccountSheet
        accountLedger={ledgerData}
        setAccountLedger={setLedgerData}
      />
    </>
  );
};

export default IngredientPurchaseHistory;
